import { Api, Events, Statistics } from "app-interfaces";
import { addMinutes, subMinutes } from "date-fns";
import React from "react";
import { v4 as uuid } from "uuid";

import { api as apiRoutes } from "./config/routes";
import { localStorage } from "./storage";
import { EventType } from "./types/StaticTypes";

type ServerError = { code: number; message: string; name: string };

export type ApiError = {
  error_code: number;
  error_message: string;
};

type PostEvents = Api.Event["postEvents"];
type ErrorCallback = (error: ApiError) => Promise<unknown>;

const passThroughDummy = async (error: ApiError) => {};

export default class ServerApi {
  private _companyId?: string | null;
  private _token = "";
  private _abortController = new AbortController();
  private readonly onError: ErrorCallback;

  // This api will probably be redone and the param removed / moved up
  constructor(params = { onError: passThroughDummy }) {
    this.onError = params.onError;
  }

  get token() {
    if (!this._token) {
      const lsToken = localStorage.getItem("token");
      if (lsToken) {
        this._token = lsToken;
      }
    }
    return this._token;
  }

  set token(params: string) {
    this._token = params;
  }

  get companyIdAsString(): string {
    if (typeof this._companyId === "undefined") {
      throw new TypeError("Company must be set before being used");
    }
    return this._companyId || "";
  }

  set companyId(id: string | null) {
    if (typeof id === "string" && !id.length) {
      throw TypeError("`id` must a be a non-empty string or null");
    }
    this._companyId = id;
  }

  get countries() {
    return this.getCountries();
  }

  get signal() {
    return this._abortController.signal;
  }

  /**
   * @param params
   * @param [signal] Allow custom signal
   */
  private apiCall = (params: Api.Config, signal?: AbortSignal) => {
    if (!params.options) {
      params.options = { method: "GET" };
    }

    _trimParameters(params.url.searchParams);

    return fetch(`${params.url}`, {
      signal: signal || this.signal,
      ...params.options,
    })
      .catch(async (error) => {
        const standardizedErrorObject = this.transformErrorObjectAndReject(error);
        await this.onError(standardizedErrorObject);
        throw error;
      })
      .then((response) =>
        response.ok
          ? response.json()
          : response.json().then(async (err) => {
              await this.onError(err);
              throw new Error(`Got non-OK response calling ${params.url}`);
            })
      );
  };

  // Error Handler
  private transformErrorObjectAndReject = (error: ServerError) => {
    if (/SyntaxError/.test(error.name)) {
      return {
        error_code: error.code,
        error_message: error.message,
      };
    }

    if (/AbortError/.test(error.name)) {
      return {
        error_code: error.code,
        error_message: error.message,
      };
    }

    const UnknownError = -1;
    return {
      error_code: error.code || UnknownError,
      error_message: error.message || "Something went wrong",
    };
  };

  // Abort api call
  abort: () => void = () => {
    this._abortController.abort();
    this._abortController = new AbortController();
  };

  // User
  loginUser: Api.Auth["login"] = ({ email, password }) => {
    const url = new URL(`${apiRoutes.auth}/login`);
    const options = {
      method: "POST",
      body: new URLSearchParams({ email: email.trim(), password: password.trim() }),
    };
    return this.apiCall({ url, options });
  };
  logoutUser: Api.Auth["logout"] = (token) => {
    const url = new URL(`${apiRoutes.auth}/logout`);
    const options = {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token ? token : this.token}`,
      },
    };
    return this.apiCall({ url, options });
  };
  recoverPass: Api.Auth["recover"] = (email) => {
    const url = new URL(`${apiRoutes.auth}/recover`);
    const options = {
      method: "POST",
      body: new URLSearchParams(email),
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    };
    return this.apiCall({ url, options });
  };
  updateEmail: Api.User["email"] = (params: { email: string }) => {
    const url = new URL(`${apiRoutes.user}/me/email`);
    const options = {
      method: "POST",
      body: new URLSearchParams(params.email),
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    };
    return this.apiCall({ url, options });
  };
  updatePassword: Api.User["password"] = (params: {
    currentPassword: string;
    newPassword: string;
  }) => {
    const { currentPassword, newPassword } = params;
    const url = new URL(`${apiRoutes.user}/me/password`);
    const options = {
      method: "POST",
      body: new URLSearchParams({
        current_password: currentPassword,
        new_password: newPassword,
      }),
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    };
    return this.apiCall({ url, options });
  };

  getProfile: Api.User["profile"] = () => {
    const url = new URL(`${apiRoutes.user}/me`);
    const options = {
      method: "GET",
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    };
    return this.apiCall({ url, options });
  };

  updateProfile: Api.User["updateProfile"] = (params: any) => {
    const url = new URL(`${apiRoutes.user}/me`);
    const options = {
      method: "POST",
      headers: this.authorizedJsonContent(),
      body: JSON.stringify(params),
    };
    return this.apiCall({ url, options });
  };

  private authorizedJsonContent() {
    return {
      Authorization: `Bearer ${this.token}`,
      "Content-Type": "application/json",
    };
  }

  getEventDetails: Api.Event["getEventDetails"] = (id) => {
    const url = new URL(`${apiRoutes.events}/${id}`);
    url.searchParams.append("company_id", this.companyIdAsString);
    const options = {
      method: "GET",
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    };
    return this.apiCall({ url, options }) as Promise<{ event: Events.IEvents }>;
  };

  getEvents = (
    params: {
      /** inclusive */
      from?: string;
      /** exclusive */
      to?: string;
      limit?: number;
      types: EventType[];
      customer_id?: string;
      session_state?: string;
      has_workout?: boolean;
    },
    signal?: AbortSignal
  ) => {
    const url = new URL(`${apiRoutes.events}`);
    url.searchParams.append("types", params.types.join(","));
    if (params.from) url.searchParams.append("from", params.from);
    if (params.to) url.searchParams.append("to", params.to);
    if (params.customer_id) url.searchParams.append("customer_id", params.customer_id);
    if (params.limit) url.searchParams.append("limit", `${params.limit}`);
    if (params.session_state) url.searchParams.append("session_state", params.session_state);
    if (params.has_workout) url.searchParams.append("has_workout", `${params.has_workout}`);
    url.searchParams.append("company_id", this.companyIdAsString);
    const callOptions = this.authorizedGetJsonOptions();
    return this.apiCall({ url, options: callOptions }, signal);
  };

  postEvents: PostEvents = (params) => {
    const url = new URL(`${apiRoutes.events}`);
    url.searchParams.append("company_id", this.companyIdAsString);
    if (!params.id) params.id = uuid();
    const options = {
      method: "POST",
      body: JSON.stringify(params),
      headers: this.authorizedJsonContent(),
    };
    return this.apiCall({ url, options });
  };

  inviteClientsToEvent: Api.Event["inviteClientsToEvent"] = (id) => {
    const url = new URL(`${apiRoutes.events}/${id}/invite`);
    url.searchParams.append("company_id", this.companyIdAsString);
    const options = {
      method: "POST",
      body: JSON.stringify({ id }),
      headers: this.authorizedJsonContent(),
    };
    return this.apiCall({ url, options });
  };
  putEvents: Api.Event["putEventId"] = (params, id) => {
    const url = new URL(`${apiRoutes.events}/${id}`);
    url.searchParams.append("company_id", this.companyIdAsString);
    if (!params.id) params.id = uuid();
    const options = {
      method: "PUT",
      body: JSON.stringify(params),
      headers: this.authorizedJsonContent(),
    };
    return this.apiCall({ url, options });
  };
  delEvent: Api.Event["delEventId"] = (id) => {
    const url = new URL(`${apiRoutes.events}/${id}`);
    const options = {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    };
    return this.apiCall({ url, options });
  };

  getStats = (params: Api.StatsParams, signal?: AbortSignal) => {
    const url = new URL(`${apiRoutes.stats}/sessions`);
    url.search = `${new URLSearchParams({ ...params })}`;
    url.searchParams.append("company_id", this.companyIdAsString);
    return this.apiCall({ url, options: this.authorizedGetJsonOptions() }, signal) as Promise<{
      stats: [Statistics.Stats];
    }>;
  };

  exportStats: Api.Stats["exportStats"] = (params) => {
    const url = new URL(`${apiRoutes.exportStats}`);
    url.search = `${new URLSearchParams({ ...params })}`;
    url.searchParams.append("company_id", this.companyIdAsString);
    const options = {
      method: "GET",
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    };
    return this.apiCall({ url, options }) as Promise<Statistics.Stats>;
  };

  getClients: Api.Client["getClients"] = () => {
    const url = new URL(`${apiRoutes.clients}`);
    url.searchParams.append("company_id", this.companyIdAsString);
    const options = {
      method: "GET",
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    };
    return this.apiCall({ url, options });
  };

  postClients: Api.Client["postClients"] = (params) => {
    const url = new URL(`${apiRoutes.clients}`);
    url.searchParams.append("company_id", this.companyIdAsString);
    const options = {
      method: "POST",
      body: JSON.stringify(params),
      headers: this.authorizedJsonContent(),
    };
    return this.apiCall({ url, options });
  };
  putClient: Api.Client["putClient"] = (params, id) => {
    const url = new URL(`${apiRoutes.clients}/${id}`);
    const options = {
      method: "PUT",
      body: JSON.stringify(params),
      headers: this.authorizedJsonContent(),
    };
    return this.apiCall({ url, options });
  };
  inviteClient: Api.Client["inviteClient"] = (email) => {
    const url = new URL(`${apiRoutes.clients}/invite`);
    url.searchParams.append("company_id", this.companyIdAsString);
    const options = {
      method: "POST",
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      body: email,
    };
    return this.apiCall({ url, options });
  };
  inviteClientBooking: Api.Client["inviteClientBooking"] = (id) => {
    const url = new URL(`${apiRoutes.clients}/${id}/invite-to-booking`);
    const options = {
      method: "POST",
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      body: new URLSearchParams(id),
    };
    return this.apiCall({ url, options });
  };
  forgetClient: Api.Client["forgetClient"] = (id) => {
    const url = new URL(`${apiRoutes.clients}/${id}/forget`);
    const options = {
      method: "POST",
      body: new URLSearchParams({ company_id: this.companyIdAsString, id }),
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    };
    return this.apiCall({ url, options });
  };

  getCountries: Api.Client["getCountries"] = () => {
    const url = new URL(`${apiRoutes.countries}`);
    const options = this.authorizedGetJsonOptions();
    return this.apiCall({ url, options });
  };

  updateImage: Api.Client["updateImage"] = (image) => {
    const url = new URL(`${apiRoutes.images}`);
    const options = {
      method: "POST",
      headers: {
        Authorization: `Bearer ${this.token}`,
        "Content-Type": "image/png",
        "Content-Disposition": 'form-data;name="image1";filename="image1.png"',
      },
      body: image,
    };
    return this.apiCall({ url, options });
  };

  // Exercises
  getExercises = (params?: { from?: number }) => {
    const callCatalog = this.getCatalog({ ...params, catalog_type: "exercises" });

    return callCatalog as Promise<Api.ExerciseCatalogResult>;
  };

  private getCatalog(params: Api.CatalogParams) {
    const url = new URL(`${apiRoutes.exercises}`);
    url.searchParams.append("catalog_type", params.catalog_type);
    if (params.from) url.searchParams.append("from", params.from + "");

    const options = this.authorizedGetJsonOptions();
    return this.apiCall({ url, options });
  }

  private authorizedGetJsonOptions() {
    return {
      method: "GET",
      headers: {
        Authorization: `Bearer ${this.token}`,
        "Content-Type": "application/json",
      },
    };
  }

  addExercises: Api.Exercise["addExercises"] = (params) => {
    Object.assign(params, { id: uuid() });
    const url = new URL(`${apiRoutes.exercises}`);
    const options = {
      method: "POST",
      headers: {
        Authorization: `Bearer ${this.token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ exercises: [params] }),
    };
    return this.apiCall({ url, options });
  };
  updateExercises: Api.Exercise["updateExercises"] = (params) => {
    const url = new URL(`${apiRoutes.exercises}`);
    const options = {
      method: "POST",
      headers: {
        Authorization: `Bearer ${this.token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ exercises: [params] }),
    };
    return this.apiCall({ url, options });
  };

  // Workouts
  getWorkouts = (params?: { from?: number }) => {
    const callCatalog = this.getCatalog({ ...params, catalog_type: "workouts" });

    return callCatalog as Promise<Api.WorkoutCatalogResult>;
  };

  addWorkouts: Api.Workout["addWorkouts"] = (params) => {
    Object.assign(params, { is_deleted: false });
    const url = new URL(`${apiRoutes.workoutsCatalog}`);
    const options = {
      method: "POST",
      headers: {
        Authorization: `Bearer ${this.token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ workouts: [params] }),
    };
    return this.apiCall({ url, options });
  };
  updateWorkouts: Api.Workout["updateWorkouts"] = (params) => {
    const url = new URL(`${apiRoutes.workoutsCatalog}`);
    const options = {
      method: "POST",
      headers: {
        Authorization: `Bearer ${this.token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ workouts: [params] }),
    };
    return this.apiCall({ url, options });
  };
  shareWorkouts: Api.Workout["shareWorkouts"] = (params) => {
    const url = new URL(`${apiRoutes.shareWorkouts}/${params.id}/share`);
    const options = {
      method: "POST",
      headers: {
        Authorization: `Bearer ${this.token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(params),
    };
    return this.apiCall({ url, options });
  };
  // Schedule
  getScheduledEvents: Api.Schedule["getScheduledEvents"] = (params) => {
    const url = new URL(`${apiRoutes.schedule}`);
    url.search = `${new URLSearchParams({ date: params.date })}`;
    url.searchParams.append("company_id", this.companyIdAsString);

    const options = {
      method: "GET",
      headers: {
        Authorization: `Bearer ${this.token}`,
        "Content-Type": "application/json",
      },
    };
    return this.apiCall({ url, options });
  };
  updateScheduledWorkouts: Api.Schedule["updateScheduleWorkout"] = ({ id, workout }) => {
    const url = new URL(`${apiRoutes.events}/${id}/workout`);
    const options = {
      method: "POST",
      headers: {
        Authorization: `Bearer ${this.token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(workout),
    };
    return this.apiCall({ url, options });
  };
  shareSession: Api.Schedule["shareSession"] = (params) => {
    const url = new URL(`${apiRoutes.events}/${params.id}/share`);
    const options = {
      method: "POST",
      headers: {
        Authorization: `Bearer ${this.token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(params),
    };
    return this.apiCall({ url, options });
  };

  // Dashboard
  getDashboardEvents: Api.Dashboard["getDashboardEvents"] = (params) => {
    const url = new URL(`${apiRoutes.dashboard}`);
    url.search = `${new URLSearchParams({ date: params.date })}`;
    url.searchParams.append("company_id", this.companyIdAsString);
    const options = {
      method: "GET",
      headers: {
        Authorization: `Bearer ${this.token}`,
        "Content-Type": "application/json",
      },
    };
    return this.apiCall({ url, options });
  };
  getSessionAvailability: Api.Dashboard["getSessionAvailability"] = (params) => {
    const { date, event_id } = params;
    const url = new URL(`${apiRoutes.dashboard}`);
    url.search = event_id
      ? `${new URLSearchParams({ date, event_id })}`
      : `${new URLSearchParams({ date })}`;
    url.searchParams.append("company_id", this.companyIdAsString);

    const options = {
      method: "GET",
      headers: {
        Authorization: `Bearer ${this.token}`,
        "Content-Type": "application/json",
      },
    };
    return this.apiCall({ url, options });
  };
  contactUs: Api.Contact["contactUs"] = (params) => {
    const url = new URL(`${apiRoutes.contact}`);
    const options = {
      method: "POST",
      headers: {
        Authorization: `Bearer ${this.token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(params),
    };
    return this.apiCall({ url, options });
  };

  /**
   * Serialize date to send it in a request to backend.
   */
  public serializeDate = (date: Date) => {
    const ISOString = adjustBrowserDateTimezone(date).toISOString();
    return removeMillisFromISODateString(ISOString);
  };
}

/**
 * Remove milliseconds from the ISO date string. For example:
 * "2021-10-12T15:58:58.629Z" -> "2021-10-12T15:58:58Z"
 *
 * The API doesn't accept ISO date with millis, responding with 400 Bad Request
 * (at least when setting "created" field for an Event), so have to remove them.
 */
function removeMillisFromISODateString(ISODateString: string) {
  return ISODateString.replace(/[.]\d{3}/, "");
}

/**
 * The API requires the clients to operate as if the user was in the UTC timezone.
 * From the API doc:
 * >All events times in the system should be treated as if user lives in UTC time.
 * >For example PT creates session for 01-09-2016 12:00 GMT+2:00 (1472738400 unix time), but system
 * >will operate with 01-09-2016 12:00 UTC time (1472731200 unix time) instead and app will show all
 * >times as if user's timezone was UTC.
 *
 * So to convert JS date, which is always in the user timezone, to the UTC timezone with the same time,
 * we have to subtract the timezone offset.
 * Use this method to adjust the JS date timezone before making API requests.
 */
function adjustBrowserDateTimezone(date: Date) {
  return subMinutes(date, date.getTimezoneOffset());
}

/**
 * API treats all timezones as UTC. JS Date object automatically applies user timezone.
 * So when we parse an API date, we need to go back from the user timezone to UTC, by adding the timezoneOffset.
 *
 * Use this method when displaying dates received from the API.
 */
export function adjustApiDateTimezone(date: Date) {
  return addMinutes(date, date.getTimezoneOffset());
}

export function _trimParameters(params: URLSearchParams) {
  const toDelete = [];
  for (const [key, value] of params.entries()) {
    if (value === "") toDelete.push(key);
  }
  toDelete.map((key) => params.delete(key));
}

const defaultValue = new ServerApi(); // will not be used, most probably, apart from in tests
defaultValue.companyId = null;
export const ServerApiContext = React.createContext(defaultValue);
